<template>
  <div class="relative">
    <img
      v-if="clickToSlide"
      id="prev"
      class="absolute left-0 z-20 ml-auto h-7 w-7 !bg-white md:hidden"
      :class="{
        hidden: !canScrollPrev,
        flex: canScrollPrev,
      }"
      src="@/assets/chevron-left.svg"
      alt="chevron"
    />
    <div
      id="tabs-row"
      class="tabs-container relative"
    >
      <div
        class="tabs"
        :class="{ 'pr-[28px]': !canScrollNext }"
      >
        <div
          v-for="(tab, index) in tabs"
          :key="tab"
          class="tab relative flex items-center gap-1 px-2 py-1 text-steel-800"
          :class="[
            modelValue === tab
              ? ['active', `text-${activeTabColor}`]
              : 'hover:opacity-80',
          ]"
          @click="selectTab(tab, index)"
        >
          {{ tab }}

          <div
            v-if="slots.rightIcon && iconCount?.includes(tab)"
            class=""
          >
            <slot name="rightIcon"></slot>
          </div>
        </div>

        <img
          v-if="clickToSlide"
          id="next"
          class="sticky -right-2 ml-auto h-7 w-7 rotate-180 bg-white md:hidden"
          :class="{
            hidden: !canScrollNext,
            flex: canScrollNext,
          }"
          src="@/assets/chevron-left.svg"
          alt="chevron"
        />
      </div>
      <div
        :class="['indicator', `bg-${activeTabColor}`]"
        :style="indicatorStyle"
      ></div>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect, onMounted, computed, nextTick, useSlots } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  tabs: Array,
  activeTabColor: {
    type: String,
    default: 'yellow-500',
  },
  iconCount: Array,
  clickToSlide: {
    type: Boolean,
    default: false,
  },
});

const slots = useSlots();

const emit = defineEmits(['update:modelValue']);
const selectedIndex = ref(props.tabs.indexOf(props.modelValue));
const tabWidths = ref([]);
const tabOffsets = ref([]);

const indicatorStyle = computed(() => ({
  width: `${tabWidths.value[selectedIndex.value] || 0}px`,
  transform: `translateX(${tabOffsets.value[selectedIndex.value] || 0}px)`,
  transition: 'transform 0.3s ease',
}));

const updateTabMeasurements = () => {
  const tabsEl = document.querySelectorAll('.tab');
  tabWidths.value = Array.from(tabsEl).map((el) => el.offsetWidth);
  tabOffsets.value = Array.from(tabsEl).map((el, index) => {
    let offset = 0;
    for (let i = 0; i < index; i++) {
      offset += tabsEl[i].offsetWidth;
    }
    return offset;
  });
};

const selectTab = (tab, index) => {
  selectedIndex.value = index;
  emit('update:modelValue', tab);
};

const canScrollPrev = ref(false);
const canScrollNext = ref(true);
const updateButtons = () => {
  const container = document.querySelector('#tabs-row');

  if (!container) return;

  canScrollPrev.value = container.scrollLeft > 0;
  canScrollNext.value =
    container.scrollLeft + container.clientWidth < container.scrollWidth - 1;
};

onMounted(() => {
  updateTabMeasurements();
  if (props.modelValue === '') {
    selectTab(props.tabs[0], 0);
  }

  if (props.clickToSlide) {
    const container = document.querySelector('#tabs-row');
    const delta = 200; // px to scroll per click

    document.querySelector('#next').addEventListener('click', () => {
      container.scrollBy({ left: delta, behavior: 'smooth' });
    });

    document.querySelector('#prev').addEventListener('click', () => {
      container.scrollBy({ left: -delta, behavior: 'smooth' });
    });

    container.addEventListener('scroll', updateButtons, { passive: true });
  }
});

watchEffect(() => {
  nextTick(updateTabMeasurements);
});
</script>

<style scoped>
.tabs-container {
  position: relative;
}

.tabs {
  display: flex;
}

@media (max-width: 768px) {
  .tabs {
    @apply w-max pb-1;
  }
  .tabs-container {
    @apply overflow-y-hidden overflow-x-scroll;
  }

  .indicator {
    @apply !bottom-1;
  }
}

.tab {
  cursor: pointer;
  transition: color 0.3s ease;
}

.indicator {
  position: absolute;
  bottom: -1px; /* Ajusta según sea necesario */
  height: 2px;
  transition: transform 0.3s ease;
}

.active {
  font-weight: 600;
  transition: color 0.1s ease;
}
</style>
